import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LetterDetailsModal = ({ letter, onClose, onDownload }) => {
  const [downloading, setDownloading] = useState(false);

  if (!letter) return null;

  const purposeLabels = {
    banking: 'Banking Purposes',
    superannuation_withdrawal: 'Superannuation Withdrawal',
    staff_reference: 'Staff Reference',
  };

  const statusLabels = {
    draft: 'Draft',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    rejected: 'Rejected',
    downloaded: 'Downloaded',
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownload?.(letter);
    } finally {
      setDownloading(false);
    }
  };

  const canDownload = letter?.letterStatus === 'approved' || letter?.letterStatus === 'downloaded';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {purposeLabels?.[letter?.letterPurpose] || 'Letter Details'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Status: {statusLabels?.[letter?.letterStatus]}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-smooth"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Letter Information */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Created Date</p>
              <p className="text-sm font-medium text-foreground">
                {new Date(letter?.createdAt)?.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
            {letter?.approvedAt && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Approved Date</p>
                <p className="text-sm font-medium text-foreground">
                  {new Date(letter?.approvedAt)?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            )}
            {letter?.hrSignatureName && (
              <>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Approved By</p>
                  <p className="text-sm font-medium text-foreground">{letter?.hrSignatureName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Title</p>
                  <p className="text-sm font-medium text-foreground">{letter?.hrSignatureTitle}</p>
                </div>
              </>
            )}
            {letter?.downloadCount > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Downloads</p>
                <p className="text-sm font-medium text-foreground">
                  {letter?.downloadCount} time{letter?.downloadCount !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>

          {/* Letter Content Preview */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-3">Letter Content</h3>
            <div className="bg-muted/30 border border-border rounded-lg p-6">
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm text-foreground leading-relaxed">
                  {letter?.generatedContent}
                </pre>
              </div>
            </div>
          </div>

          {/* AI Generation Info */}
          {letter?.aiModelUsed && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex gap-2">
                <Icon name="Sparkles" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-foreground mb-1">AI Generated</p>
                  <p className="text-muted-foreground">
                    This letter was generated using {letter?.aiModelUsed} based on your employment data.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Close
          </Button>
          {canDownload && (
            <Button
              variant="default"
              iconName="Download"
              onClick={handleDownload}
              disabled={downloading}
              className="flex-1"
            >
              {downloading ? 'Downloading...' : 'Download PDF'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LetterDetailsModal;